// src/oura/components/docs/WordComments.tsx
//
// The comments and replies inside a Word file (#4565), read from the .docx by
// the backend (`wordFile.comments`). They are not Google Drive comments, so the
// drawer's threads are empty for a Word file and these would not show at all.
// Read-only: each conversation is grouped under the text it is on, oldest
// first, the way it reads in Word. Converting the file to a Google Doc brings
// them in as threads Oura tracks.

import type { WordComment } from "@service/aeo/clientDocs";
import { DrawerBlock, SURFACE, TYPE } from "@oura/ui";
import { docDate } from "@oura/docs";
import { cn } from "../../lib/cn";

/** Comments grouped by the text they are on, in the order each group first appears in the file. */
function byQuoted(comments: readonly WordComment[]): Array<{ quoted: string | null; comments: WordComment[] }> {
    const groups: Array<{ quoted: string | null; comments: WordComment[] }> = [];
    for (const c of comments) {
        const key = c.quoted?.trim() || null;
        let g = groups.find((x) => x.quoted === key);
        if (!g) {
            g = { quoted: key, comments: [] };
            groups.push(g);
        }
        g.comments.push(c);
    }
    for (const g of groups) g.comments.sort((a, b) => (a.at ?? "").localeCompare(b.at ?? ""));
    return groups;
}

export function WordComments({ comments }: { comments: readonly WordComment[] }) {
    const groups = byQuoted(comments);
    return (
        <DrawerBlock label={`Comments in the Word file · ${comments.length}`}>
            <p className={cn(TYPE.tc, "m-0 text-gray-500")}>
                Read from the file, read-only. Convert it to a Google Doc to work with them as questions Oura tracks.
            </p>
            <ul className="m-0 mt-2 list-plain p-0">
                {groups.map((g, i) => (
                    <li key={`${g.quoted ?? "none"}-${i}`} className={cn("border-t py-2.5 first:border-t-0", SURFACE.hairline)}>
                        {g.quoted && (
                            <p className={cn(TYPE.tc, "m-0 border-l-2 border-gray-200 pl-2 italic text-gray-500")}>{g.quoted}</p>
                        )}
                        <ul className="m-0 mt-1.5 list-plain space-y-1.5 p-0">
                            {g.comments.map((c) => (
                                <li key={c.id} className="min-w-0">
                                    <p className={cn(TYPE.tc, "m-0 text-gray-500")}>
                                        <span className="font-medium text-gray-900">{c.author || "Someone"}</span>
                                        {docDate(c.at) ? ` · ${docDate(c.at)}` : ""}
                                    </p>
                                    <p className={cn(TYPE.tb, "m-0 whitespace-pre-line break-words text-gray-700")}>{c.text}</p>
                                </li>
                            ))}
                        </ul>
                    </li>
                ))}
            </ul>
        </DrawerBlock>
    );
}
